import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SCENE_CHECK_PASSWORD, SCENE_DEFAULT } from 'consts/configCustom';
import { useDispatch, useSelector } from 'react-redux';
import adminApi from 'services/adminApi';
import { setCheckPassCode } from 'store/actions';
import './style.scss';

const PopupPasscode = ({ selectSceneById = () => {} }, ref) => {
  const { t } = useTranslation();
  const {
    isShow,
    level,
    nextScene,
    isFirstTime,
    isFirstTimeLoading,
    isShowCancelButton,
  } = useSelector((state) => state.checkPassCode) || {};
  const scenes = useSelector((state) => state.scenes);

  const [passcode, setPasscode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();

  const refLevel = useRef(0);

  const handlePasscodeChange = (event) => {
    setPasscode(event.target.value);
    setErrorMessage('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    queryPassword({ passcode });
    return;
  };

  const queryPassword = async ({ passcode }) => {
    try {
      const res = await adminApi({
        url: `/password/check-password`,
        method: 'POST',
        body: { password: passcode },
      });
      const { data } = await res.json();
      const finalLevel = data.level || 0;
      if (finalLevel >= refLevel.current) {
        handleRemoveError();
        dispatch(
          setCheckPassCode({
            isShow: false,
            level: finalLevel,
            isAccess: true,
            isShowCancelButton: true,
          })
        );
        setTimeout(() => {
          selectSceneById(nextScene?.gId, nextScene?.sId);
        }, 100);

        window.introAnimation = false;
      } else {
        setErrorMessage(t('incorrect_passcode_permission'));
      }
    } catch (error) {
      setErrorMessage(t('incorrect_passcode'));
    }
  };

  const handleRemoveError = () => {
    setErrorMessage('');
    setPasscode('');
  };

  const handleClose = () => {
    handleRemoveError();
    dispatch(
      setCheckPassCode({
        isShow: false,
        isAccess: true,
        nextScene: { gId: null, sId: null },
      })
    );
    isFirstTime &&
      setTimeout(() => {
        selectSceneById(SCENE_DEFAULT.groupId, SCENE_DEFAULT.sceneId);
      }, 100);
  };

  const handleCheckPasscode = async ({ gId, sId, isFirstTime } = {}) => {
    if (!scenes.length) return false;
    const scenePassword = SCENE_CHECK_PASSWORD.find(
      (item) => item.id === sId
    ) || { level: 1 };
    if (!scenePassword) return false;

    if (scenePassword && level < scenePassword.level) {
      refLevel.current = scenePassword.level;
      dispatch(
        setCheckPassCode({
          isShow: isFirstTime ? false : true,
          isAccess: false,
          nextScene: { gId, sId },
          isFirstTime,
        })
      );
      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    isHasPasscode: handleCheckPasscode,
  }));

  if (!isShow || !isFirstTimeLoading) return null;

  return (
    <div className="passcode-container">
      <div className="popup-passcode">
        <div className="popup-passcode-content">
          <div
            className="popup-passcode-close"
            hidden={!isShowCancelButton}
            onClick={handleClose}
          >
            ✖️
          </div>
          <h1 className="popup-passcode-title">{t('enter_passcode')}</h1>
          <form onSubmit={handleSubmit} className="form-passcode">
            <div className="input-container">
              <input
                type="password"
                value={passcode}
                onChange={handlePasscodeChange}
                placeholder={t('please_enter_your_passcode_here')}
                className="input-passcode"
                ref={(element) => element?.focus?.()}
              />
              <p className={`error-message`}>{errorMessage}</p>
            </div>
            <button
              type="submit"
              className={`access-popup-pc ${passcode === '' ? 'disabled' : ''}`}
              disabled={passcode === ''}
            >
              {t('access')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default forwardRef(PopupPasscode);
